import type { ReactNode } from "react";
import {
  Info,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

/**
 * Callout — the tinted notice family pulled from the UI-pitch concept (the
 * soft-tint + hairline-border + dark-text triad Hugo liked, incl. the
 * AI-insight treatment). One shared component so every surface speaks the same
 * visual language instead of re-deriving ad-hoc banners.
 *
 * DESIGN.md compliance: every tone works in BOTH light and dark — tints are
 * `bg-<hue>-500/10` (alpha, mode-agnostic), borders `border-<hue>-500/20`, and
 * text uses the `text-<hue>-700 dark:text-<hue>-300` pairing (never a bare
 * `-400` shade as body text). No raw hex, no `hsl(var())`.
 */
export type CalloutTone = "info" | "good" | "warn" | "insight" | "neutral";

const TONE: Record<
  CalloutTone,
  { wrap: string; icon: string; title: string; defaultIcon: LucideIcon }
> = {
  info: {
    wrap: "bg-sky-500/10 border-sky-500/20",
    icon: "text-sky-600 dark:text-sky-400",
    title: "text-sky-800 dark:text-sky-200",
    defaultIcon: Info,
  },
  good: {
    wrap: "bg-emerald-500/10 border-emerald-500/20",
    icon: "text-emerald-600 dark:text-emerald-400",
    title: "text-emerald-800 dark:text-emerald-200",
    defaultIcon: CheckCircle2,
  },
  warn: {
    wrap: "bg-amber-500/10 border-amber-500/25",
    icon: "text-amber-600 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-200",
    defaultIcon: AlertTriangle,
  },
  insight: {
    // the AI-insight treatment — primary-tinted, Sparkles. Matches CardInsight.
    wrap: "bg-primary/5 border-primary/20",
    icon: "text-primary",
    title: "text-foreground",
    defaultIcon: Sparkles,
  },
  neutral: {
    wrap: "bg-muted/50 border-border",
    icon: "text-muted-foreground",
    title: "text-foreground",
    defaultIcon: Info,
  },
};

export function Callout({
  tone = "info",
  title,
  icon,
  actions,
  className = "",
  children,
}: {
  tone?: CalloutTone;
  /** Optional bold lead line above the body. */
  title?: ReactNode;
  /** Override the tone's default icon; pass null to omit the icon entirely. */
  icon?: LucideIcon | null;
  /** Right-aligned actions (buttons/links). */
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const t = TONE[tone];
  const Icon = icon === null ? null : (icon ?? t.defaultIcon);

  return (
    <div
      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${t.wrap} ${className}`}
    >
      {Icon && <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${t.icon}`} />}
      <div className="min-w-0 flex-1">
        {title && (
          <p className={`font-medium leading-snug ${t.title}`}>{title}</p>
        )}
        {children && (
          <div className={`leading-snug text-foreground/80 ${title ? "mt-0.5" : ""}`}>
            {children}
          </div>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Paired-token status pill (the concept's s-pending/s-reviewed/s-noted chips).
 * Same both-modes discipline as Callout.
 */
export type PillTone = "info" | "good" | "warn" | "neutral";

const PILL: Record<PillTone, string> = {
  info: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
  good: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  warn: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  neutral: "bg-muted text-muted-foreground",
};

export function StatusPill({
  tone = "neutral",
  children,
}: {
  tone?: PillTone;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${PILL[tone]}`}
    >
      {children}
    </span>
  );
}
